import React, { useState, useMemo, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";

import {
    Grid,
    Box,
    TextField,
    IconButton,
    Typography,
    Chip,
    CircularProgress,
    GlobalStyles,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { Check, Cancel, Visibility, Close } from "@mui/icons-material";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import {
    publishTrackRequest
} from "../../Redux/Reducers/TracksReducer";
import { getAlbumPublishRequests, publishRequestActionSingle } from "../../Redux/Reducers/AlbumsReducer";

import { useTranslation } from "react-i18next";
import { formatUTCDateForDataGrid } from "../../utils/dateUtils";
import { formatEnumLabel } from "../../utils/formatEnumLabel";
import { CustomLoadingOverlay } from "../../components/common/TableLoader";

function TracksPublishRequestsManagement() {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { publishRequests } = useSelector((state) => state.albums);
    const publishRequestsData = publishRequests.data || [];
    const loading = publishRequests.loading;
    const pagination = publishRequests.pagination;
    const [searchText, setSearchText] = useState("");
    const [actionLoading, setActionLoading] = useState({});

    const { user } = useSelector((state) => state.auth || state.AuthReducer || {});

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 10,
    });

    const [podcasterModalOpen, setPodcasterModalOpen] = useState(false);
    const [selectedPodcaster, setSelectedPodcaster] = useState(null);

    const [albumModalOpen, setAlbumModalOpen] = useState(false);
    const [selectedTrackDetails, setSelectedTrackDetails] = useState(null);

    // Fetch 
    useEffect(() => {
        dispatch(
            getAlbumPublishRequests({
                type: "TRACK",
                page: paginationModel.page + 1,
                limit: paginationModel.pageSize,
                search: searchText,
            })
        );
    }, [paginationModel, searchText, dispatch]);

    const handlePublishRequestAction = async (requestId, actionType) => {
        const actionKey = `${requestId}_${actionType}`;
        try {
            setActionLoading(prev => ({ ...prev, [actionKey]: true }));
            const payload = {
                action: actionType // APPROVE or REJECT
            };

            await dispatch(
                publishRequestActionSingle({
                    requestId,
                    payload
                })
            ).unwrap();

            const actionMap = {
                REJECT: "rejected",
                APPROVE: "approved",
            };

            toast.success(t("albums.tracksPublishRequests.messages.publish_request_action_success", {
                action: actionMap[actionType]
            }));

            dispatch(
                getAlbumPublishRequests({
                    type: "TRACK",
                    page: paginationModel.page + 1,
                    limit: paginationModel.pageSize,
                    search: searchText,
                })
            );

        } catch (error) {
            toast.error(error || "Something went wrong please try again later");
        } finally {
            setActionLoading(prev => ({ ...prev, [actionKey]: false }));
        }
    };

    const columns = [
        {
            field: 'sno',
            headerName: t('albums.tracksPublishRequests.fields.sno', 'S.No'),
            width: 80,
            sortable: false,
            renderCell: (params) => {
                const rowsInCurrentPage = params.api.getRowsCount();
                const allRows = params.api.getAllRowIds();
                const currentRowIndex = allRows.indexOf(params.id);
                const visibleRowIndex = currentRowIndex % paginationModel.pageSize;
                return paginationModel.page * paginationModel.pageSize + visibleRowIndex + 1;
            }
        },
        {
            field: "albumTitle",
            headerName: t('albums.tracksPublishRequests.fields.album_title', 'Album Title'),
            flex: 1
        },
        {
            field: "trackTitle",
            headerName: t('albums.tracksPublishRequests.fields.track_title', "Track Title"),
            flex: 1
        },
        {
            field: "requestedAt",
            headerName: t('albums.tracksPublishRequests.fields.requested_at', "Requested At"),
            flex: 1,
            valueFormatter: (value) => {
                return formatUTCDateForDataGrid(value);
            }
        },
        {
            field: "status",
            headerName: t('albums.tracksPublishRequests.fields.status', "Status"),
            flex: 1,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color={params.value === "PUBLISH" ? "success" : "warning"}
                    variant="outlined"
                    size="small"
                />
            )
        },
        {
            field: "actions",
            headerName: t('albums.tracksPublishRequests.fields.actions', "Actions"),
            width: 250,
            sortable: false,
            renderCell: (params) => {

                return (
                    <Box sx={{ display: 'flex', gap: '6px' }}>
                        <IconButton
                            size="small"
                            title={t('albums.albumDetails.title', "View Album Details")}
                            onClick={() => {
                                setSelectedTrackDetails(params.row.track);
                                setAlbumModalOpen(true);
                            }}
                            sx={{
                                color: '#ffffff',
                                backgroundColor: "rgba(227,214,245,0.2) !important",
                                width: "36px",
                                height: "36px",
                                border: "1px solid rgba(227,214,245,0.1)",
                                padding: "0"
                            }}
                        >
                            <Visibility sx={{ fontSize: 20 }} />
                        </IconButton>
                        {((params.row.originalStatus === "PENDING_APPROVAL" || params.row.originalStatus === "PENDING") && (user.roleName != "Podcaster" || user.roleId != 3)) && (
                            <>
                                <IconButton
                                    size="small"
                                    disabled={actionLoading[`${params.row.backendId}_approve`] || actionLoading[`${params.row.backendId}_reject`]}
                                    title={t('albums.tracksPublishRequests.actions.approve_publish_request', "Approve Publish Request")}
                                    onClick={() =>
                                        handlePublishRequestAction(params.row.backendId, "approve")
                                    }
                                    sx={{ color: '#ffffff', backgroundColor: 'rgba(227, 214, 245, 0.2) !important', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', width: '36px', height: '36px' }}
                                >
                                    {actionLoading[`${params.row.backendId}_approve`] ? (
                                        <CircularProgress size={16} sx={{ color: 'white' }} />
                                    ) : (
                                        <Check />
                                    )}
                                </IconButton>

                                <IconButton
                                    size="small"
                                    disabled={actionLoading[`${params.row.backendId}_approve`] || actionLoading[`${params.row.backendId}_reject`]}
                                    title={t('albums.tracksPublishRequests.actions.reject_publish_request', "Reject Publish Request")}
                                    onClick={() =>
                                        handlePublishRequestAction(params.row.backendId, "reject")
                                    }
                                    sx={{ color: '#ffffff', backgroundColor: 'rgba(227, 214, 245, 0.2) !important', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', width: '36px', height: '36px' }}
                                >
                                    {actionLoading[`${params.row.backendId}_reject`] ? (
                                        <CircularProgress size={16} sx={{ color: 'white' }} />
                                    ) : (
                                        <Cancel />
                                    )}
                                </IconButton>
                            </>
                        )}
                    </Box>
                );
            },
        }
    ];

    const rows = useMemo(() => {
        if (!Array.isArray(publishRequestsData) || publishRequestsData.length === 0) {
            return [];
        }

        return publishRequestsData.map((publishRequest, index) => {
            if (!publishRequest || !publishRequest.requestId) {
                console.warn('Invalid publish Request at index', index, publishRequest);
                return null;
            }

            return {
                id: publishRequest.requestId,
                backendId: publishRequest.requestId,
                albumId: publishRequest.albumId,
                sno: index + 1,
                status: formatEnumLabel(publishRequest?.track?.status || publishRequest?.status),
                originalStatus: publishRequest?.track?.status || publishRequest?.status,
                albumTitle: publishRequest?.track?.album?.title || null,
                trackTitle: publishRequest?.track?.title || null,
                track: publishRequest?.track || null,
                requestedAt: publishRequest.requestedAt || null,
                podcaster: publishRequest.podcaster || null,
                album: publishRequest.album || publishRequest?.track?.album || null,
            };
        }).filter(Boolean); // Remove any null entries
    }, [publishRequestsData]);

    return (
        <Box sx={{ p: '36px 24px 24px' }}>
            {/* Header */}
            <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('albums.tracksPublishRequests.title', 'Episodes Scheduling & Publishing')}
                </Typography>

                <Box display="flex" gap={2}>
                    <TextField
                        size="small"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={t('search', 'Search')}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '999px',
                                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                                color: '#fff',
                                height: '38px',
                                '& fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'rgba(230, 210, 249, 0.1)',
                                    borderWidth: '1px',
                                },
                            },
                            '& .MuiOutlinedInput-input': {
                                padding: '10px 16px',
                                color: '#FF69B4',
                                fontSize: '15px',
                                lineHeight: '18px',

                            },
                            '& input::placeholder': {
                                color: 'rgba(255,255,255,0.6)',
                                opacity: 1,
                            },
                            '& .MuiFormHelperText-root': {
                                ml: '20px'
                            }
                        }}
                    />

                </Box>
            </Box>

            {/* Table */}
            <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    slots={{ toolbar: GridToolbar, loadingOverlay: CustomLoadingOverlay }}
                    slotProps={{
                        toolbar: {
                            csvOptions: { disableToolbarButton: true }, // Removes the "Export as CSV" menu item
                            printOptions: { disableToolbarButton: true }, // Removes the "Print" menu item
                        },
                    }}
                    paginationMode="server"
                    rowCount={pagination?.total || 0}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        setPaginationModel(model);
                    }}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                        sorting: { sortModel: [{ field: "nameEn", sort: "asc" }] }
                    }}
                    getRowId={(row) => row.id || row.sno}
                    pageSizeOptions={[5, 10, 25]}
                    disableRowSelectionOnClick
                    localeText={
                        i18n.language?.toLowerCase().startsWith("fr")
                            ? frFR.components.MuiDataGrid.defaultProps.localeText
                            : enUS.components.MuiDataGrid.defaultProps.localeText
                    }
                    sx={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        borderRadius: '0',
                        '& .MuiDataGrid-toolbarContainer': {
                            padding: '12px',
                            margin: '0 -12px',
                            gap: '10px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-topContainer::after': {
                            display: 'none',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
                            color: '#fff',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            borderRadius: '999px',
                            textTransform: 'none',
                            fontWeight: 500,
                            padding: '6px 12px',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                        },
                        '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
                            width: '18px',
                            height: '18px',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            maxHeight: 'auto',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            borderRadius: '0 !important',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone': {
                            position: 'inherit',
                        },
                        '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
                            maxHeight: 'none !important',
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-cell': {
                            height: 'auto !important',
                            minHeight: 'var(--height)',
                            paddingTop: '5px',
                            paddingBottom: '5px',
                        },
                        '& .MuiDataGrid-cell>.MuiBox-root': {
                            minHeight: '100%',
                            alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
                            borderRadius: '0 !important',
                            borderColor: 'rgba(230, 210, 249, 0.2) !important',
                            color: '#fff',
                        },
                        '& .MuiDataGrid-columnHeaders [role=row]': {
                            backgroundColor: '#1a1a1a',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderColor: 'rgba(230, 210, 249, 0.2) !important'
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
                            color: 'white',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
                            outline: 'none',
                        },
                        '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                            opacity: '0.8 !important',
                            fill: 'white',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined': {
                            border: '0 !important',
                            height: 'auto',
                            fontWeight: '600',
                        },
                        '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
                            color: "#03c11c",
                        },
                        '& .MuiTablePagination-root': {
                            color: '#fff'
                        },
                        '& .MuiTablePagination-selectLabel': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-input': {
                            marginRight: '10px',
                        },
                        '& .MuiTablePagination-input .MuiSelect-select': {
                            paddingTop: '2px',
                            paddingBottom: '2px',
                            border: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            borderRadius: '4px',
                        },
                        '& .MuiTablePagination-input .MuiSvgIcon-root': {
                            fill: 'white'
                        },
                        '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
                            marginBottom: '0',
                        },
                        '& .MuiTablePagination-actions': {
                            marginLeft: '10px !important',
                        },
                        '& .MuiTablePagination-actions .MuiButtonBase-root': {
                            color: '#FF69B4',
                        },
                        '& .MuiDataGrid-filler': {
                            height: 'auto !important',
                        },
                        '& .MuiDataGrid-filler>div': {
                            borderColor: 'transparent',
                        },
                        '& .MuiDataGrid-overlayWrapper': {
                            height: 'auto !important',
                            maxHeight: '50px',
                        },
                        '& .MuiDataGrid-overlayWrapperInner': {
                            minHeight: '40px',
                            height: 'auto !important'
                        },
                        '& .MuiDataGrid-overlay': {
                            color: '#fff',
                            borderTop: '1px solid rgba(230, 210, 249, 0.2)',
                            backgroundColor: 'transparent',
                            minHeight: '40px',
                        },
                    }}
                />
            </Box>

            {/* Podcaster Details Modal */}
            <Dialog open={podcasterModalOpen} onClose={() => setPodcasterModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {t('podcaster_details', "Podcaster Details")}
                    <IconButton onClick={() => setPodcasterModalOpen(false)} sx={{ padding: '0', color: 'white' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(230, 210, 249, 0.1)', padding: '24px 16px' }}>
                    {selectedPodcaster ? (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Typography sx={{ fontSize: '15px' }}><strong>Name:</strong> {selectedPodcaster.name || 'N/A'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Email:</strong> {selectedPodcaster.email || 'N/A'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Phone:</strong> {selectedPodcaster.phone || 'N/A'}</Typography>
                            {selectedPodcaster.bio && <Typography sx={{ fontSize: '15px' }}><strong>Bio:</strong> {selectedPodcaster.bio}</Typography>}
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '15px', color: 'rgba(255,255,255,0.7)' }}>No podcaster details available.</Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* Track Details Modal */}
            <Dialog open={albumModalOpen} onClose={() => setAlbumModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ padding: '16px', fontSize: '18px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {t('albums.albumDetails.track_details', "Track Details")}
                    <IconButton onClick={() => setAlbumModalOpen(false)} sx={{ padding: '0', color: 'white' }}>
                        <Close />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: 'rgba(230, 210, 249, 0.1)', padding: '24px 16px' }}>
                    {selectedTrackDetails ? (
                        <Box display="flex" flexDirection="column" gap={2}>
                            <Typography sx={{ fontSize: '15px' }}><strong>Title:</strong> {selectedTrackDetails.title || 'N/A'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Track ID:</strong> {selectedTrackDetails.id || 'N/A'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Album Title:</strong> {selectedTrackDetails.album?.title || 'N/A'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Track Number:</strong> {selectedTrackDetails.trackNumber ?? 'N/A'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Description:</strong> {selectedTrackDetails.description || 'N/A'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Duration:</strong> {selectedTrackDetails.durationSeconds != null ? `${selectedTrackDetails.durationSeconds}s` : 'N/A'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Explicit Content:</strong> {selectedTrackDetails.isExplicit == null ? 'N/A' : selectedTrackDetails.isExplicit ? 'Yes' : 'No'}</Typography>
                            <Typography sx={{ fontSize: '15px' }}><strong>Status:</strong> {selectedTrackDetails.status || 'N/A'}</Typography>
                            {selectedTrackDetails.album?.coverImage && (
                                <Typography sx={{ fontSize: '15px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <strong>Cover Image:</strong> 
                                    <img src={selectedTrackDetails.album.coverImage} alt="Cover" style={{ width: '60px', height: '60px', borderRadius: '8px', objectFit: 'cover' }} />
                                </Typography>
                            )}
                        </Box>
                    ) : (
                        <Typography sx={{ fontSize: '15px', color: 'rgba(255,255,255,0.7)' }}>No album details available.</Typography>
                    )}
                </DialogContent>
            </Dialog>

            <GlobalStyles
                styles={{
                    '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
                        backgroundColor: '#252525 !important',
                        border: '1px solid rgba(230, 210, 249, 0.1)',
                        fontSize: '15px',
                        color: '#fff',
                        borderRadius: '4px',
                    },
                    '.MuiDialog-root .MuiPaper-root': {
                        borderRadius: '20px',
                    },
                    '.MuiDataGrid-columnsManagementHeader': {
                        padding: '10px !important',
                    },
                    '.MuiInputBase-formControl': {
                        fontSize: '14px !important',
                        color: 'white !important',
                        borderRadius: '999px !important',
                    },
                    '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
                        borderTop: '1px solid rgba(230, 210, 249, 0.1)',
                        padding: '4px 8px 4px 16px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement': {
                        padding: '0px 16px 12px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
                        color: '#FF69B4',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
                        color: 'white',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
                        borderColor: 'rgba(230, 210, 249, 0.1) !important',
                    },
                    '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
                        display: 'none',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
                        padding: '0',
                    },
                    '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
                        fontSize: '14px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
                        padding: '5px 10px',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
                        fill: '#FF69B4',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
                        borderColor: 'rgba(230, 210, 249, 0.3) !important',
                        margin: '0',
                    },
                    '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
                        minWidth: '32px',
                    },
                    '.MuiDataGrid-scrollbarFiller': {
                        borderTop: 'none !important',
                    },
                    '& .MuiDataGrid-scrollbar': {
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: 'rgba(230, 210, 249, 0.3)',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: 'rgba(230, 210, 249, 0.5)',
                        },
                    },
                }}
            />
        </Box>
    );
}

export default TracksPublishRequestsManagement;
